import { types as sdkTypes } from '../../util/sdkLoader';
import { transitions } from '../../transactions/transactionProcessPurchase';
import { CANCEL_TRANSITIONS, COMPLETE_TRANSITIONS } from '../DashboardPage/dashboardStats';

const { Money } = sdkTypes;

const HOUR_MS = 60 * 60 * 1000;

// Matches expire-payment's P3D in ext/transaction-processes/default-purchase,
// and the 72h server/crons/handleOfferExpired.js gives accepted offers.
export const PAYMENT_WINDOW_HOURS = 72;

export const STATUS_AWAITING_PAYMENT = 'awaitingPayment';
export const STATUS_READY_TO_SHIP = 'readyToShip';
export const STATUS_SHIPPED = 'shipped';
export const STATUS_COMPLETED = 'completed';
export const STATUS_DISPUTED = 'disputed';
export const STATUS_CANCELED = 'canceled';

const REVIEW_TRANSITIONS = [
  transitions.REVIEW_1_BY_PROVIDER,
  transitions.REVIEW_2_BY_PROVIDER,
  transitions.REVIEW_1_BY_CUSTOMER,
  transitions.REVIEW_2_BY_CUSTOMER,
  transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  transitions.EXPIRE_REVIEW_PERIOD,
];

const STATUS_BY_TRANSITION = {
  [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: STATUS_AWAITING_PAYMENT,
  [transitions.CONFIRM_PAYMENT]: STATUS_READY_TO_SHIP,
  [transitions.MARK_DELIVERED]: STATUS_SHIPPED,
  [transitions.OPERATOR_MARK_DELIVERED]: STATUS_SHIPPED,
  [transitions.DISPUTE]: STATUS_DISPUTED,
  [transitions.OPERATOR_DISPUTE]: STATUS_DISPUTED,
  ...Object.fromEntries(COMPLETE_TRANSITIONS.map(t => [t, STATUS_COMPLETED])),
  ...Object.fromEntries(REVIEW_TRANSITIONS.map(t => [t, STATUS_COMPLETED])),
  ...Object.fromEntries(CANCEL_TRANSITIONS.map(t => [t, STATUS_CANCELED])),
};

// Last transitions of orders that have been paid for. Pending Buy It Now
// payments (request-payment) are left out, like in the inbox: PayPal hasn't
// charged the buyer yet.
export const PAID_ORDER_TRANSITIONS = Object.keys(STATUS_BY_TRANSITION).filter(
  t => t !== transitions.REQUEST_PAYMENT_AFTER_INQUIRY
);

// Accepting an offer only sets offerPrice = customerOfferPrice in metadata
// (OfferButtons.js). The tx stays in state/inquiry until the buyer pays:
// paypal-verify-order fires request-payment-after-inquiry right before
// confirm-payment. Same check as server/crons/handleOfferExpired.js.
const INQUIRY_TRANSITIONS = [transitions.INQUIRE, transitions.BACK_TO_INQUIRY];

const isAcceptedOffer = tx => {
  const { lastTransition, metadata = {} } = tx?.attributes || {};
  const { offerPrice, customerOfferPrice } = metadata || {};
  return (
    INQUIRY_TRANSITIONS.includes(lastTransition) &&
    customerOfferPrice > 0 &&
    offerPrice === customerOfferPrice
  );
};

// Accepted offers the buyer still has to pay. Inquiries without an accepted
// offer are dropped by getOrderStatus. Queried with meta_isActive=true, the
// same scoping InboxPage.duck.js uses.
export const AWAITING_PAYMENT_TRANSITIONS = [
  ...INQUIRY_TRANSITIONS,
  transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
];

export const getOrderStatus = tx =>
  isAcceptedOffer(tx)
    ? STATUS_AWAITING_PAYMENT
    : STATUS_BY_TRANSITION[tx?.attributes?.lastTransition] || null;

// What the current user has to do next: sellers ship paid orders and chase
// unpaid accepted offers; buyers pay and confirm receipt.
export const isActionRequired = (status, isSale) =>
  isSale
    ? status === STATUS_AWAITING_PAYMENT || status === STATUS_READY_TO_SHIP
    : status === STATUS_AWAITING_PAYMENT || status === STATUS_SHIPPED;

const transitionTimes = (tx, names) =>
  (tx?.attributes?.transitions || [])
    .filter(t => names.includes(t.transition))
    .map(t => new Date(t.createdAt));

const firstTransitionTime = (tx, names) => transitionTimes(tx, names)[0] || null;
const lastTransitionTime = (tx, names) => transitionTimes(tx, names).pop() || null;

const lastTransitionedAt = tx => {
  const at = tx?.attributes?.lastTransitionedAt;
  return at ? new Date(at) : null;
};

// OfferButtons resets timeOfOffer to the acceptance time.
const offerAcceptedAt = tx => {
  const at = isAcceptedOffer(tx) ? tx.attributes.metadata.timeOfOffer : null;
  return at ? new Date(at) : null;
};

// When the card was bought: payment confirmed, or for an unpaid accepted
// offer, when the offer was accepted.
export const getOrderDate = tx =>
  lastTransitionTime(tx, [transitions.CONFIRM_PAYMENT]) ||
  offerAcceptedAt(tx) ||
  lastTransitionTime(tx, [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]) ||
  lastTransitionedAt(tx);

export const getShippedDate = tx =>
  lastTransitionTime(tx, [transitions.MARK_DELIVERED, transitions.OPERATOR_MARK_DELIVERED]) ||
  lastTransitionedAt(tx);

export const getCompletedDate = tx =>
  lastTransitionTime(tx, COMPLETE_TRANSITIONS) || lastTransitionedAt(tx);

export const getCanceledDate = tx =>
  lastTransitionTime(tx, CANCEL_TRANSITIONS) || lastTransitionedAt(tx);

// expire-payment fires 72h after the transaction FIRST entered pending-payment;
// an unpaid accepted offer expires 72h after acceptance.
export const hoursLeftToPay = (tx, now = new Date()) => {
  const start =
    offerAcceptedAt(tx) ||
    firstTransitionTime(tx, [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]) ||
    lastTransitionedAt(tx);
  if (!start) {
    return null;
  }
  const deadline = start.getTime() + PAYMENT_WINDOW_HOURS * HOUR_MS;
  return Math.max(0, Math.ceil((deadline - now.getTime()) / HOUR_MS));
};

export const getTracking = tx => {
  const tracking = tx?.attributes?.metadata?.TrackingLink;
  return typeof tracking === 'string' && tracking.trim() ? tracking.trim() : null;
};

// Sellers can enter a tracking URL or just a carrier/number ("Add info or
// tracking link"); only real web links get a "View Tracking" button.
export const getTrackingUrl = tx => {
  const tracking = getTracking(tx);
  if (!tracking) {
    return null;
  }
  const candidate = /^https?:\/\//i.test(tracking) ? tracking : null;
  try {
    return candidate ? new URL(candidate).href : null;
  } catch (e) {
    return null;
  }
};

const otherParty = (tx, isSale) => (isSale ? tx?.customer : tx?.provider);

export const getOtherPartyName = (tx, isSale) =>
  otherParty(tx, isSale)?.attributes?.profile?.displayName || '';

// An accepted offer has no payinTotal until checkout; its offer price is in
// dollars, like everywhere else in transaction metadata.
export const getOrderAmount = tx =>
  tx?.attributes?.payinTotal?.amount ||
  (isAcceptedOffer(tx) ? Math.round(tx.attributes.metadata.offerPrice * 100) : 0);

// Offer prices are USD, like the dashboard's offer activity (dashboardStats.js).
export const getOrderTotal = tx => {
  const { payinTotal } = tx?.attributes || {};
  const amount = getOrderAmount(tx);
  return payinTotal || (amount ? new Money(amount, 'USD') : null);
};

/**
 * Turns transaction entities into rows for the Orders page.
 *
 * @param {Array<Object>} transactions denormalised transaction entities
 * @param {boolean} isSale true on the Selling tab
 * @returns {Array<Object>} { tx, status, actionRequired, date }
 */
export const toOrders = (transactions, isSale) =>
  transactions
    .map(tx => {
      const status = getOrderStatus(tx);
      return status
        ? { tx, status, actionRequired: isActionRequired(status, isSale), date: getOrderDate(tx) }
        : null;
    })
    .filter(Boolean);

export const FILTER_ALL = 'all';
export const FILTER_ACTION_REQUIRED = 'actionRequired';

export const matchesFilter = (order, filter) =>
  filter === FILTER_ALL
    ? true
    : filter === FILTER_ACTION_REQUIRED
    ? order.actionRequired
    : order.status === filter;

export const countByFilter = (orders, filters) =>
  filters.reduce(
    (counts, filter) => ({
      ...counts,
      [filter]: orders.filter(o => matchesFilter(o, filter)).length,
    }),
    {}
  );

export const matchesSearch = (order, query, isSale) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) {
    return true;
  }
  const { tx } = order;
  const haystack = [
    tx?.listing?.attributes?.title,
    getOtherPartyName(tx, isSale),
    getTracking(tx),
    tx?.id?.uuid,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const SORT_NEWEST = 'newest';
export const SORT_OLDEST = 'oldest';
export const SORT_PRICE_HIGH = 'priceHigh';
export const SORT_PRICE_LOW = 'priceLow';

const time = d => (d ? d.getTime() : 0);

const COMPARATORS = {
  [SORT_NEWEST]: (a, b) => time(b.date) - time(a.date),
  [SORT_OLDEST]: (a, b) => time(a.date) - time(b.date),
  [SORT_PRICE_HIGH]: (a, b) => getOrderAmount(b.tx) - getOrderAmount(a.tx),
  [SORT_PRICE_LOW]: (a, b) => getOrderAmount(a.tx) - getOrderAmount(b.tx),
};

export const sortOrders = (orders, sort) =>
  [...orders].sort(COMPARATORS[sort] || COMPARATORS[SORT_NEWEST]);
